import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/Select';
import { apiService } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { Link as LinkIcon, BarChart3, Eye, Calendar, Search, Filter, Trash2, Edit2, Power, RefreshCw, Globe } from 'lucide-react';

interface ShortUrl {
  _id: string;
  shortCode: string;
  originalUrl: string;
  urlType: string;
  clientUsername: string;
  accessCount: number;
  isActive: boolean;
  createdAt: string;
  lastAccessed: string | null;
  fullShortUrl: string;
}

interface Stats {
  total: number;
  active: number;
  totalAccesses: number;
  createdToday: number;
}

export default function ShortUrls() {
  const { isAdmin } = useAuth();
  const [shortUrls, setShortUrls] = useState<ShortUrl[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);

  // Filtros
  const [filterType, setFilterType] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Seleção múltipla
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [selectAll, setSelectAll] = useState(false);

  // Modals
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDomainModal, setShowDomainModal] = useState(false);
  const [editingUrl, setEditingUrl] = useState<ShortUrl | null>(null);

  // Form data
  const [editForm, setEditForm] = useState({
    originalUrl: '',
    urlType: '',
    clientUsername: ''
  });

  const [domainForm, setDomainForm] = useState({
    oldDomain: '',
    newDomain: ''
  });

  // Carregar links
  const loadShortUrls = useCallback(async () => {
    try {
      setIsLoading(true);
      setError('');

      const response = await apiService.getShortUrls({
        page: currentPage,
        limit: itemsPerPage,
        urlType: filterType || undefined
      });

      const urls = response.data?.shortUrls || response.shortUrls || response.data || [];
      setShortUrls(Array.isArray(urls) ? urls : []);
      
      // Atualizar paginação
      const pagination = response.data?.pagination || {};
      setTotalPages(pagination.pages || 0);
      setTotalItems(pagination.total || 0);

    } catch (err: any) {
      setError(err.message || 'Erro ao carregar links encurtados');
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, itemsPerPage, filterType]);

  useEffect(() => {
    loadShortUrls();
  }, [loadShortUrls]);

  // Calcular estatísticas (usar total items da paginação)
  const stats: Stats = {
    total: totalItems,
    active: shortUrls.filter(url => url.isActive).length,
    totalAccesses: shortUrls.reduce((sum, url) => sum + url.accessCount, 0),
    createdToday: shortUrls.filter(url => {
      const createdDate = new Date(url.createdAt);
      const today = new Date();
      return createdDate.toDateString() === today.toDateString();
    }).length
  };

  // Filtrar URLs apenas por busca (tipo já é filtrado no backend)
  const filteredUrls = shortUrls.filter(url => {
    const matchesSearch = !searchTerm || 
      url.shortCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
      url.clientUsername.toLowerCase().includes(searchTerm.toLowerCase()) ||
      url.originalUrl.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  // Seleção múltipla
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredUrls.map(url => url._id));
    }
    setSelectAll(!selectAll);
  };

  const handleSelect = (id: string) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(selectedId => selectedId !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  // Editar
  const handleEdit = (url: ShortUrl) => {
    setEditingUrl(url);
    setEditForm({
      originalUrl: url.originalUrl,
      urlType: url.urlType,
      clientUsername: url.clientUsername
    });
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    if (!editingUrl) return;

    try {
      await apiService.updateShortUrl(editingUrl._id, editForm);
      alert('Link atualizado com sucesso!');
      setShowEditModal(false);
      loadShortUrls();
    } catch (err: any) {
      alert(`Erro ao atualizar link: ${err.message}`);
    }
  };

  // Deletar
  const handleDelete = async (id: string) => {
    if (!window.confirm('Deseja realmente deletar este link?')) return;

    try {
      await apiService.deleteShortUrl(id);
      alert('Link deletado com sucesso!');
      loadShortUrls();
    } catch (err: any) {
      alert(`Erro ao deletar link: ${err.message}`);
    }
  };

  // Deletar selecionados
  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) {
      alert('Selecione ao menos um link para deletar');
      return;
    }

    if (!window.confirm(`Deseja realmente deletar ${selectedIds.length} link(s)?`)) return;

    try {
      const result = await apiService.bulkDeleteShortUrls(selectedIds);
      alert(`${result.deletedCount} link(s) deletado(s) com sucesso!`);
      setSelectedIds([]);
      setSelectAll(false);
      loadShortUrls();
    } catch (err: any) {
      alert(`Erro ao deletar links: ${err.message}`);
    }
  };

  // Alterar domínio em massa
  const handleDomainChange = async () => {
    if (!domainForm.oldDomain || !domainForm.newDomain) {
      alert('Preencha ambos os domínios');
      return;
    }

    if (!window.confirm(`Deseja alterar todos os links de:\n${domainForm.oldDomain}\n\npara:\n${domainForm.newDomain}?`)) return;

    try {
      const result = await apiService.bulkDomainChange(domainForm.oldDomain, domainForm.newDomain);
      alert(`${result.updatedCount} link(s) atualizado(s) com sucesso!`);
      setShowDomainModal(false);
      setDomainForm({ oldDomain: '', newDomain: '' });
      loadShortUrls();
    } catch (err: any) {
      alert(`Erro ao alterar domínios: ${err.message}`);
    }
  };

  // Toggle ativo
  const handleToggle = async (id: string) => {
    try {
      await apiService.toggleShortUrl(id);
      loadShortUrls();
    } catch (err: any) {
      alert(`Erro ao alternar estado: ${err.message}`);
    }
  };

  // Copiar link
  const handleCopy = (url: string) => {
    navigator.clipboard.writeText(url);
    alert('Link copiado!');
  };

  // Formatação de data relativa
  const getTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (seconds < 60) return 'agora';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
    if (seconds < 2592000) return `${Math.floor(seconds / 86400)}d`;
    return date.toLocaleDateString('pt-BR');
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <LinkIcon className="h-8 w-8" />
            Links Encurtados
          </h2>
          <p className="text-muted-foreground mt-2">
            Gerencie todos os links encurtados do sistema
          </p>
        </div>
        <div className="flex gap-2">
          {isAdmin() && (
            <Button onClick={() => setShowDomainModal(true)} variant="outline">
              <Globe className="h-4 w-4 mr-2" />
              Alterar Domínio
            </Button>
          )}
          {selectedIds.length > 0 && (
            <Button onClick={handleBulkDelete} variant="destructive">
              <Trash2 className="h-4 w-4 mr-2" />
              Deletar Selecionados ({selectedIds.length})
            </Button>
          )}
          <Button onClick={loadShortUrls} variant="outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            Atualizar
          </Button>
        </div>
      </div>

      {/* Estatísticas */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total de Links</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
              <LinkIcon className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Links Ativos</p>
                <p className="text-2xl font-bold">{stats.active}</p>
              </div>
              <Eye className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total de Acessos</p>
                <p className="text-2xl font-bold">{stats.totalAccesses}</p>
              </div>
              <BarChart3 className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Criados Hoje</p>
                <p className="text-2xl font-bold">{stats.createdToday}</p>
              </div>
              <Calendar className="h-8 w-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filtros */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filtros
          </CardTitle>
        </CardHeader>
        <CardContent className="flex gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium mb-1">Tipo de Link</label>
            <Select value={filterType} onValueChange={(value) => {
              setFilterType(value);
              setCurrentPage(1); // Resetar para página 1 ao mudar filtro
            }}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Todos os tipos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">Todos os tipos</SelectItem>
                <SelectItem value="mpegts">MPEG-TS</SelectItem>
                <SelectItem value="m3u8">M3U8</SelectItem>
                <SelectItem value="hls">HLS</SelectItem>
                <SelectItem value="ssiptv">SSIPTV</SelectItem>
                <SelectItem value="app_download">App Download</SelectItem>
                <SelectItem value="client_urls">Client URLs</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium mb-1">Buscar</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                type="text"
                placeholder="Código ou nome..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
          <div className="w-40">
            <label className="block text-sm font-medium mb-1">Por página</label>
            <Select value={itemsPerPage.toString()} onValueChange={(value) => {
              setItemsPerPage(parseInt(value));
              setCurrentPage(1); // Resetar para página 1 ao mudar limite
            }}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Lista de URLs */}
      <Card>
        <CardHeader>
          <CardTitle>Links Encurtados ({filteredUrls.length})</CardTitle>
          <CardDescription>Gerencie todos os links encurtados do sistema</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8">Carregando...</div>
          ) : error ? (
            <div className="text-center py-8 text-red-500">{error}</div>
          ) : filteredUrls.length === 0 ? (
            <div className="text-center py-12">
              <LinkIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg font-medium">Nenhum link encontrado</p>
              <p className="text-gray-400 text-sm mt-2">Links encurtados aparecerão aqui</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="p-3 text-left">
                      <input
                        type="checkbox"
                        checked={selectAll}
                        onChange={handleSelectAll}
                        className="rounded"
                      />
                    </th>
                    <th className="p-3 text-left text-sm font-semibold text-gray-700">Código</th>
                    <th className="p-3 text-left text-sm font-semibold text-gray-700">Cliente</th>
                    <th className="p-3 text-left text-sm font-semibold text-gray-700">Tipo</th>
                    <th className="p-3 text-left text-sm font-semibold text-gray-700">URL Original</th>
                    <th className="p-3 text-center text-sm font-semibold text-gray-700">Acessos</th>
                    <th className="p-3 text-center text-sm font-semibold text-gray-700">Status</th>
                    <th className="p-3 text-center text-sm font-semibold text-gray-700">Criado</th>
                    <th className="p-3 text-center text-sm font-semibold text-gray-700">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredUrls.map((url) => (
                    <tr key={url._id} className="hover:bg-gray-50 transition-colors">
                      <td className="p-3">
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(url._id)}
                          onChange={() => handleSelect(url._id)}
                          className="rounded"
                        />
                      </td>
                      <td className="p-3">
                        <button
                          onClick={() => handleCopy(url.fullShortUrl)}
                          className="font-mono text-sm bg-blue-50 text-blue-700 px-2 py-1 rounded hover:bg-blue-100 transition-colors"
                        >
                          {url.shortCode}
                        </button>
                      </td>
                      <td className="p-3 text-sm">{url.clientUsername}</td>
                      <td className="p-3">
                        <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full">
                          {url.urlType}
                        </span>
                      </td>
                      <td className="p-3 text-sm text-gray-600 truncate max-w-xs">
                        {url.originalUrl}
                      </td>
                      <td className="p-3 text-center text-sm font-semibold">{url.accessCount}</td>
                      <td className="p-3 text-center">
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          url.isActive 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-gray-100 text-gray-700'
                        }`}>
                          {url.isActive ? 'Ativo' : 'Inativo'}
                        </span>
                      </td>
                      <td className="p-3 text-center text-sm text-gray-500">
                        {getTimeAgo(url.createdAt)}
                      </td>
                      <td className="p-3">
                        <div className="flex gap-2 justify-center">
                          <button
                            onClick={() => handleEdit(url)}
                            className="p-1 hover:bg-blue-50 rounded transition-colors"
                            title="Editar"
                          >
                            <Edit2 className="h-4 w-4 text-blue-600" />
                          </button>
                          <button
                            onClick={() => handleToggle(url._id)}
                            className="p-1 hover:bg-yellow-50 rounded transition-colors"
                            title="Ativar/Desativar"
                          >
                            <Power className={`h-4 w-4 ${url.isActive ? 'text-green-600' : 'text-gray-400'}`} />
                          </button>
                          <button
                            onClick={() => handleDelete(url._id)}
                            className="p-1 hover:bg-red-50 rounded transition-colors"
                            title="Deletar"
                          >
                            <Trash2 className="h-4 w-4 text-red-600" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          
          {/* Paginação */}
          {!isLoading && filteredUrls.length > 0 && totalPages > 1 && (
            <div className="flex items-center justify-between mt-6 pt-6 border-t">
              <div className="text-sm text-gray-600">
                Mostrando {((currentPage - 1) * itemsPerPage) + 1} a {Math.min(currentPage * itemsPerPage, totalItems)} de {totalItems} links
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  variant="outline"
                  size="sm"
                >
                  Primeira
                </Button>
                <Button
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  variant="outline"
                  size="sm"
                >
                  Anterior
                </Button>
                <div className="flex items-center gap-2 px-4">
                  <span className="text-sm text-gray-600">
                    Página {currentPage} de {totalPages}
                  </span>
                </div>
                <Button
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  variant="outline"
                  size="sm"
                >
                  Próxima
                </Button>
                <Button
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                  variant="outline"
                  size="sm"
                >
                  Última
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal de Edição */}
      {showEditModal && editingUrl && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <h3 className="text-xl font-bold mb-4">Editar Link Encurtado</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">URL Original</label>
                <Input
                  value={editForm.originalUrl}
                  onChange={(e) => setEditForm({ ...editForm, originalUrl: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Tipo</label>
                <Select
                  value={editForm.urlType}
                  onValueChange={(value) => setEditForm({ ...editForm, urlType: value })}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mpegts">MPEG-TS</SelectItem>
                    <SelectItem value="m3u8">M3U8</SelectItem>
                    <SelectItem value="hls">HLS</SelectItem>
                    <SelectItem value="ssiptv">SSIPTV</SelectItem>
                    <SelectItem value="app_download">App Download</SelectItem>
                    <SelectItem value="client_urls">Client URLs</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Nome do Cliente</label>
                <Input
                  value={editForm.clientUsername}
                  onChange={(e) => setEditForm({ ...editForm, clientUsername: e.target.value })}
                />
              </div>
            </div>
            <div className="flex gap-2 mt-6">
              <Button onClick={handleSaveEdit} className="flex-1">
                Salvar
              </Button>
              <Button onClick={() => setShowEditModal(false)} variant="outline" className="flex-1">
                Cancelar
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Alteração de Domínio */}
      {showDomainModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <h3 className="text-xl font-bold mb-4">Alterar Domínio em Massa</h3>
            <p className="text-sm text-gray-600 mb-4">
              Esta ação irá substituir o domínio antigo pelo novo em todos os links encurtados. 
              <strong> Apenas administradores</strong> podem executar esta ação.
            </p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Domínio Antigo</label>
                <Input
                  placeholder="http://cliente.getu9.online"
                  value={domainForm.oldDomain}
                  onChange={(e) => setDomainForm({ ...domainForm, oldDomain: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Domínio Novo</label>
                <Input
                  placeholder="http://novodominio.com"
                  value={domainForm.newDomain}
                  onChange={(e) => setDomainForm({ ...domainForm, newDomain: e.target.value })}
                />
              </div>
            </div>
            <div className="flex gap-2 mt-6">
              <Button onClick={handleDomainChange} className="flex-1">
                Alterar Domínio
              </Button>
              <Button onClick={() => setShowDomainModal(false)} variant="outline" className="flex-1">
                Cancelar
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
